import * as React from 'react';

import { IBuyboxCallbacks, IBuyboxData, IBuyboxResources, IBuyboxState, IBuyboxViewProps } from '@msdyn365-commerce-modules/buybox';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Alert, getTelemetryObject, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync, IImageSettings } from '@msdyn365-commerce/core';
import { IBuyboxProps, IBuyboxResources as IBuyboxViewResources } from '../../definition-extensions/buybox.ext.props.autogenerated';
import { IBuyboxData as IBuyboxViewData } from '../buybox.data';
import AddToCart, { IAddToCartComponentProps, IAddToCartFailureResult } from './add-to-cart-component';
import { getConfigureErrors, getGenericError, getQuantityError } from './utilities/error-utilities';

export interface IBuyboxAddToCartViewProps {
    ContainerProps: INodeProps;
    errorBlock?: React.ReactNode;
    button?: React.ReactNode;
    isError?: boolean; // VSI Customization
    isAddedToCart?: boolean; // VSI Customization - To check if product added to cart on clicking 'Addtobag' button
}

export interface IBuyboxErrorBlockProps {
    configureErrors: { [configureId: string]: string | undefined };
    quantityError?: string;
    customAmountError?: string;
    otherError?: string;
    resources: IBuyboxResources;
    showError: boolean;
}
/* VSI Customization - START - 25/01/21 */
export interface IErrorMessage {
    SetIsError: React.Dispatch<React.SetStateAction<boolean>>;
}
/* VSI Customization - END */
let telemetryContent: ITelemetryContent;
export const BuyboxErrorBlock: React.FC<IBuyboxErrorBlockProps & IErrorMessage> = ({
    showError,
    configureErrors,
    quantityError,
    customAmountError,
    otherError,
    resources,
    SetIsError
}) => {
    let errorMessages: (string | undefined)[] = [];

    errorMessages = Object.values(configureErrors).filter(message => message !== undefined);

    if (quantityError) {
        errorMessages.push(quantityError);
        SetIsError(true);
    }

    if (customAmountError) {
        errorMessages.push(customAmountError);
    }

    if (otherError) {
        errorMessages.push(otherError);
        SetIsError(true);
    }

    return (
        <Alert
            isOpen={showError && errorMessages.length > 0}
            color='danger'
            assertive={true}
            aria-label={resources.buyboxErrorMessageHeader}
        >
            <div className='msc-alert__header ' aria-hidden='true'>
                <div className=' __add-to-cart-error-alert'>
                    <span className='msi-exclamation-triangle' />
                    <span>{resources.buyboxErrorMessageHeader}</span>
                </div>
            </div>
            {errorMessages.map(message => {
                if (message) {
                    return <div className='msc-alert__line'>{message}</div>;
                } else {
                    return null;
                }
            })}
        </Alert>
    );
};
/* Upgraded - 10.0.16 - START */
export const RetailDefaultOrderQuantityLimitsFeatureName: string = 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature';
const _isOrderQuantityLimitsFeatureEnabled = (props: IBuyboxProps<IBuyboxData>): boolean | undefined => {
    const isFeatureEnabledInHq = props.data.featureState.result?.find(
        featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName
    )?.IsEnabled;
    if (!isFeatureEnabledInHq) {
        return false;
    }
    const defaultOrderQuantityLimitsFeatureConfig = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
    if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
        return false;
    }

    if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
        return true;
    }

    const customerInfo = props.data.customerInformation.result;
    return (
        customerInfo &&
        ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
            (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
    );
};
/* Upgraded - END */
// tslint:disable-next-line: max-func-body-length
export default function getBuyboxAddToCart(
    props: IBuyboxViewProps & IBuyboxProps<IBuyboxData & IBuyboxViewData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    resources: IBuyboxViewResources,
    isAgeRestricted: boolean,
    defaultMinimumKeyInPrice: number,
    defaultMaximumKeyInPrice: number,
    onAddToCartClicked?: () => void
): IBuyboxAddToCartViewProps {
    const {
        id,
        typeName,
        context,
        config: { minimumKeyInPrice, maximumKeyInPrice },
        slots: { plantsPotsProductsSlot },
        data: {
            cart: { result: cart },
            product: { result: product },
            productAvailableQuantity: { result: productAvailableQuantity },
            productAttributes: { result: productAttributes },
            plantPotsProductCountState: { result: plantPotsProductCountState }
        }
    } = props;
    // Check if there is pots products, display popup otherwise not
    const plantPotsProductCount = plantPotsProductCountState?.potsProductCount || 0;
    const {
        request: {
            app: {
                config: { fulfillmentAttributeName, fulfillmentAttributeTextValue }
            }
        }
    } = props.context;
    const attributeName = fulfillmentAttributeName
        ? fulfillmentAttributeName
              .toString()
              .toLowerCase()
              .trim()
        : 'fulfillmenttype';
    const attributeTextValue = fulfillmentAttributeTextValue
        ? fulfillmentAttributeTextValue
              .toString()
              .toLowerCase()
              .trim()
        : 'plants';

    const fulfillmentTypeAttribute = productAttributes?.find(attribute => attribute?.Name?.toLowerCase() === attributeName);
    const isPlantFulfillment = fulfillmentTypeAttribute && fulfillmentTypeAttribute.TextValue?.toLowerCase() === attributeTextValue;
    const [isError, SetIsError] = React.useState(false);
    const {
        quantity,
        errorState: { configureErrors, quantityError, otherError, errorHost, customAmountError },
        selectedProduct,
        isUpdatingDimension,
        isServiceItem,
        isUpdatingDeliveryOptions,
        keyInPriceAmount
    } = state;

    const productAvailability = state.productAvailableQuantity
        ? state.productAvailableQuantity.ProductAvailableQuantity
        : productAvailableQuantity && productAvailableQuantity.length
        ? productAvailableQuantity[0].ProductAvailableQuantity
        : undefined;

    const onAddToCartFailed = (result: IAddToCartFailureResult) => {
        let quantityErrorForState: string | undefined;
        let customAmountErrorForState: string | undefined;
        let otherErrorForState: string | undefined = getGenericError(
            result,
            cart,
            resources,
            context,
            product,
            productAvailability,
            undefined
        );
        if (result.failureReason === 'OUTOFSTOCK') {
            quantityErrorForState = result.failureReason === 'OUTOFSTOCK' ? getQuantityError(result.stockLeft, resources) : undefined;
        } else if (
            result.failureReason === 'CARTACTIONFAILED' &&
            result.cartActionResult &&
            (result.cartActionResult.substatus === 'MAXQUANTITY' || result.cartActionResult.substatus === 'QUANTITYLIMITS')
        ) {
            quantityErrorForState = getGenericError(result, cart, resources, context, product, productAvailability, undefined);
            otherErrorForState = undefined; // prevent error duplication in otherError and quantityError
        }
        if (result.failureReason === 'INVALIDCUSTOMAMOUNT') {
            if (keyInPriceAmount === undefined || keyInPriceAmount < (minimumKeyInPrice || defaultMinimumKeyInPrice)) {
                const formatedAmountLimit = props.context.cultureFormatter.formatCurrency(
                    minimumKeyInPrice || defaultMinimumKeyInPrice,
                    context.request.channel?.Currency
                );
                customAmountErrorForState = resources.invalidSmallCustomAmountText.replace('{minAmount}', formatedAmountLimit);
            } else if (keyInPriceAmount > (maximumKeyInPrice || defaultMaximumKeyInPrice)) {
                const formatedAmountLimit = props.context.cultureFormatter.formatCurrency(
                    maximumKeyInPrice || defaultMaximumKeyInPrice,
                    context.request.channel?.Currency
                );
                customAmountErrorForState = resources.invalidLargeCustomAmountText.replace('{maxAmount}', formatedAmountLimit);
            }
        }
        callbacks.updateErrorState({
            errorHost: 'ADDTOCART',
            quantityError: quantityErrorForState,
            configureErrors:
                result.failureReason === 'MISSINGDIMENSION'
                    ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard)
                    : {},
            customAmountError: customAmountErrorForState,
            otherError: otherErrorForState
        });
    };

    const dialogStrings = {
        goToCartText: resources.buyBoxGoToCartText,
        continueShoppingText: resources.buyBoxContinueShoppingText,
        headerItemOneText: resources.buyBoxSingleItemText,
        headerItemFormatText: resources.buyBoxMultiLineItemFormatText,
        headerMessageText: resources.buyBoxHeaderMessageText,
        freePriceText: resources.priceFree,
        originalPriceText: resources.originalPriceText,
        currentPriceText: resources.currentPriceText,
        ageRestrictionPopupHeading: resources.ageRestrictionPopupHeading,
        ageRestrictionNote: resources.ageRestrictionNote,
        ageConfirmation: resources.ageConfirmation,
        ageVerificationDetails: resources.ageVerificationDetails,
        cancelButtonLabel: resources.cancelButtonLabel,
        buyBoxHeaderMessageText: resources.buyBoxHeaderMessageText,
        buyBoxGoToCartText: resources.buyBoxGoToCartText
    };
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: `w=240&h=240&m=6`, w: 0, h: 0 },
            lg: { q: `w=240&h=240&m=6`, w: 0, h: 0 },
            xl: { q: `w=240&h=240&m=6`, w: 0, h: 0 }
        },
        lazyload: true
    };

    const isLoading = props.data.productAvailableQuantity.status === 'LOADING';
    const isLoadingDeliveryOptions = typeName !== 'quickview' && props.data.deliveryOptions?.status === 'LOADING';
    if (isLoadingDeliveryOptions && callbacks.changeUpdatingDeliveryOptions) {
        callbacks.changeUpdatingDeliveryOptions(false);
    }
    if (isLoading) {
        callbacks.changeUpdatingDimension(false);
    }
    telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);

    // check if the product delivery mode is electronic
    const emailDeliveryModeCode = props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode;
    const deliveryOptions = props.data.deliveryOptions?.result;
    const isEmailDelivery =
        deliveryOptions &&
        deliveryOptions.DeliveryOptions &&
        ArrayExtensions.hasElements(deliveryOptions.DeliveryOptions) &&
        deliveryOptions.DeliveryOptions[0].Code === emailDeliveryModeCode;
    let addToCartResult;
    if (product) {
        const addToCartComponentProps: IAddToCartComponentProps = {
            addToCartText: resources.addToCartText,
            outOfStockText: resources.outOfStockTextForHd,
            addToCartTextHd: resources.addToCartTextHd,
            navigationUrl: getUrlSync('cart', context.actionContext),
            quantity: quantity,
            data: {
                product: product,
                price: state.productPrice,
                cart: props.data.cart.result,
                customerInformation: props.data.customerInformation.result
            },
            isAgeRestrictedProduct: isAgeRestricted, // To display age restriction popup
            isFulfillmentProduct: isPlantFulfillment /* && plantPotsProductCount > 0 */, // To display popup against fulfillment type products and if there is any product(s) available
            plantsPotsProductsSlot: plantsPotsProductsSlot && plantsPotsProductsSlot[0], // To display pots products on clicking add-to-bag button for plant products
            potsProductCount: plantPotsProductCount,
            context: context,
            id: id,
            typeName: typeName,
            isUpdatingDeliveryOptions: isUpdatingDeliveryOptions,
            onError: onAddToCartFailed,
            isLoadingDeliveryOptions: isLoadingDeliveryOptions,
            changeUpdatingDeliveryOptions: callbacks.changeUpdatingDeliveryOptions,
            getSelectedProduct: selectedProduct,
            productAvailability: productAvailability,
            isLoading: isLoading,
            isUpdatingDimension: isUpdatingDimension,
            changeUpdatingDimension: callbacks.changeUpdatingDimension,
            dialogStrings: dialogStrings,
            gridSettings: props.context.request.gridSettings,
            imageSettings: defaultImageSettings,
            telemetryContent: telemetryContent,
            isAddServiceItemToCart: isServiceItem,
            isPriceKeyedIn: state.isPriceKeyedIn,
            customPriceAmount: state.keyInPriceAmount,
            isCustomPriceSelected: state.isCustomPriceSelected,
            maximumKeyInPrice: props.config.maximumKeyInPrice,
            minimumKeyInPrice: props.config.minimumKeyInPrice,
            defaultMinimumKeyInPrice: defaultMinimumKeyInPrice,
            defaultMaximumKeyInPrice: defaultMaximumKeyInPrice,
            isOrderQuantityLimitsFeatureEnabled: _isOrderQuantityLimitsFeatureEnabled(props),
            isAddEmailDeliveryItemToCart: isEmailDelivery,
            onAddToCartClicked: onAddToCartClicked,
            deliveryOptions: props.data.deliveryOptions.result
        };
        addToCartResult = AddToCart(addToCartComponentProps);
    }
    return {
        ContainerProps: {
            className: 'ms-buybox__add-to-cart-container'
        },
        button: addToCartResult && addToCartResult.addToCartButton,
        errorBlock: (
            <BuyboxErrorBlock
                configureErrors={configureErrors}
                quantityError={quantityError}
                otherError={otherError}
                customAmountError={customAmountError}
                resources={resources}
                showError={errorHost === 'ADDTOCART'}
                SetIsError={SetIsError}
            />
        ),
        isError: isError,
        isAddedToCart: addToCartResult && addToCartResult.isAddedToCart
    };
}
